import { FormikProps } from 'formik';
import React, {
  MutableRefObject,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { AnyObject } from 'yup';
import { useAppSelector } from '../../../../app/hooks';
import {
  OnboardFormStepEnum,
  selectOnboardFormStep,
} from '../../onboardingSlice';
import { CRUDProvider, CRUDProviderProps } from './context/CRUDContext';
import { FieldsAreaGridConfig } from './crud/components/FieldsArea';
import { GenericFieldProps } from './crud/components/GenericFormField';
import {
  SubmitButtonConfig,
  SubmitButtonProps,
} from './crud/components/SubmitButton';
import { CreateForm } from './crud/CreateForm';
import { DeleteForm, DeleteFormProps } from './crud/DeleteForm';
import { UpdateForm } from './crud/UpdateForm';
import { fixedForwardRef } from './crud/utils/forwardRefFix';
import { ViewForm } from './crud/ViewForm';

export interface SlotProps<T extends AnyObject> {
  SubmitButton?: {
    renderFn?: (props: SubmitButtonProps<Partial<T>>) => React.ReactElement;
    config?: SubmitButtonConfig;
  };
  gridConfig?: FieldsAreaGridConfig;
  // TODO: create other types such as textfield, radio, etc. to have rich control over them
  // like we have for dropdown component
  fieldsConfig?: {
    [K in keyof Partial<T> | string]: GenericFieldProps;
  };
  hiddenFields?: (keyof T)[];

  textOverrides?: {
    entityIsNotSelected?: string | JSX.Element;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmitValueTransformer?: (value: T) => any;
}

export interface GenericFormProps<T extends AnyObject, K extends AnyObject>
  extends Omit<CRUDProviderProps<T, K>, 'children'> {
  slots?: {
    [OnboardFormStepEnum.Add]?: {
      renderBefore?: React.FC;
      renderAfter?: React.FC;
      component?: React.FC<SlotProps<T>>;
      props?: SlotProps<T>;
    };
    [OnboardFormStepEnum.Edit]?: {
      renderBefore?: React.FC;
      renderAfter?: React.FC;
      component?: React.FC<SlotProps<K>>;
      props?: SlotProps<K>;
    };
    [OnboardFormStepEnum.View]?: {
      renderBefore?: React.FC;
      renderAfter?: React.FC;
      component?: React.FC<SlotProps<K>>;
      props?: SlotProps<K>;
    };
    [OnboardFormStepEnum.Delete]?: {
      renderBefore?: React.FC;
      renderAfter?: React.FC;
      component?: React.FC<DeleteFormProps<K>>;
      props?: DeleteFormProps<K>;
    };
  };
}

export interface BaseFormControls {
  getFieldValue: (fieldKey: string) => string | number | AnyObject;
  setFieldValue: (fieldKey: string, value: string | number) => void;
  getFormikBag: () => MutableRefObject<FormikProps<AnyObject>>;
}

export interface FormController {
  [OnboardFormStepEnum.Add]: MutableRefObject<BaseFormControls | null>;
  [OnboardFormStepEnum.Edit]: MutableRefObject<BaseFormControls | null>;
  [OnboardFormStepEnum.Delete]: MutableRefObject<BaseFormControls | null>;
  [OnboardFormStepEnum.View]: MutableRefObject<BaseFormControls | null>;
}

export const GenericForm = fixedForwardRef(function <
  T extends AnyObject,
  K extends AnyObject
>(props: GenericFormProps<T, K>, ref?: React.Ref<FormController>) {
  const currOnboardingFormStep = useAppSelector(selectOnboardFormStep);
  const createFormRef = useRef<BaseFormControls | null>(null);
  const updateFormRef = useRef<BaseFormControls | null>(null);
  const deleteFormRef = useRef<BaseFormControls | null>(null);
  const viewFormRef = useRef<BaseFormControls | null>(null);

  const slotDefaults = {
    [OnboardFormStepEnum.Add]: (
      <CreateForm
        {...((props?.slots?.[OnboardFormStepEnum.Add]?.props as SlotProps<T>) ||
          {})}
        ref={createFormRef}
      />
    ),
    [OnboardFormStepEnum.Edit]: (
      <UpdateForm
        {...((props?.slots?.[OnboardFormStepEnum.Edit]
          ?.props as SlotProps<K>) || {})}
        ref={updateFormRef}
      />
    ),
    [OnboardFormStepEnum.Delete]: (
      <DeleteForm
        {...((props?.slots?.[OnboardFormStepEnum.Delete]
          ?.props as SlotProps<K>) || {})}
        ref={deleteFormRef}
      />
    ),
    [OnboardFormStepEnum.View]: (
      <ViewForm
        {...((props?.slots?.[OnboardFormStepEnum.View]
          ?.props as SlotProps<K>) || {})}
        ref={viewFormRef}
      />
    ),
  };

  const content = useMemo(() => {
    const customComponent = props?.slots?.[currOnboardingFormStep]?.component;
    const renderBefore = props?.slots?.[currOnboardingFormStep]?.renderBefore;
    const renderAfter = props?.slots?.[currOnboardingFormStep]?.renderAfter;

    const renderContent = () => {
      return (
        <>
          {renderBefore && renderBefore({})}
          {customComponent
            ? customComponent(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (props?.slots?.[currOnboardingFormStep]?.props as any) || {}
              )
            : slotDefaults[currOnboardingFormStep]}
          {renderAfter && renderAfter({})}
        </>
      );
    };

    return renderContent();
  }, [props, currOnboardingFormStep]);

  const formsController = {
    [OnboardFormStepEnum.Add]: createFormRef,
    [OnboardFormStepEnum.Edit]: updateFormRef,
    [OnboardFormStepEnum.Delete]: deleteFormRef,
    [OnboardFormStepEnum.View]: viewFormRef,
  };

  useImperativeHandle(ref, () => formsController);

  return <CRUDProvider {...props}>{content}</CRUDProvider>;
});
